import { all, takeLatest, call, put, select } from 'redux-saga/effects';
import { ActionType } from 'deox';
import { navigate } from '@reach/router';
import serviceDirectoryService from '@/services/service-directory';
import apiPortalService from '@/services/api-portal';
import membershipService from '@/services/membership';
import env from '@/env';
import { AxiosError } from 'axios';
import sortBy from 'lodash/sortBy';

import { AppsInfo, Article, AssetsSocial, Categories, Category, ComplianceLevel, DocOverview, Entity, EntryType, FeatureItems, Paging } from '@/types';
import {
  fetchEntityActions,
  fetchEntityByIdActions,
  createOrUpdateEntityActions,
  fetchAppsInfoActions,
  fetchListEntityActions,
  fetchCategoriesActions,
  createAppInfoActions,
  editAppInfoActions,
  deleteAppInfoActions,
  rateEntityActions,
  rateAssetActions,
  fetchAssetSocialActions,
  deleteEntityActions,
  fetchComplianceLevelsActions,
  generateIndexesActions,
  fetchListUserSocialActions,
  fetchArticlesActions,
  createArticleActions,
  editArticleActions,
  deleteArticleActions,
  fetchArticlesBySlugActions,
  fetchDocsOverviewActions,
  updateDocOverviewActions,
  fetchCategoryByIdActions,
  createCategoryActions,
  editCategoryActions,
  deleteCategoryActions,
  scrapeAppInfoActions,
  loadMoreArticlesActions,
  createAndScrapeAppInfoActions,
  getFeatureItemsActions,
} from '../actions/service-directory';
import { RootState } from '../reducers';

const PAGE_SIZE_DEFAULT = 10;
export function* fetchEntityByIdSaga(action: ActionType<typeof fetchEntityByIdActions.request>) {
  try {
    const { entityId } = action.payload;
    const entity: Entity = yield call(serviceDirectoryService.fetchEntityById, entityId);
    yield put(fetchEntityByIdActions.success(entity));
  } catch (err) {
    const errorResponse = (err as AxiosError)?.response;
    const message: any = (err as any)?.message;
    const errors: unknown = {
      status: errorResponse?.status,
      message:
        errorResponse?.data?.message ||
        errorResponse?.data?.error_description ||
        errorResponse?.data?.errors?.[0].message ||
        message,
    };
    yield put(fetchEntityByIdActions.failure(errors));
  }
}

export function* fetchEntitySaga(action: ActionType<typeof fetchEntityActions.request>) {
  try {
    const { subdomain } = action.payload;
    const entity: Entity = yield call(serviceDirectoryService.fetchEntity, subdomain);
    const {data} = yield select((state: RootState) => state.authUser);
    if (!data && !entity.isPublic) {
      navigate('/auth/login');
      return;
    }
    yield put(fetchEntityActions.success(entity));
  } catch (err) {
    const errorResponse = (err as AxiosError)?.response;
    const message: any = (err as any)?.message;
    const errors = {
      status: errorResponse?.status,
      message:
        errorResponse?.data?.message ||
        errorResponse?.data?.error_description ||
        errorResponse?.data?.errors?.[0].message ||
        message,
    };
    yield put(fetchEntityActions.failure(errors));
  }
}

export function* createOrUpdateEntitySaga(
  action: ActionType<typeof createOrUpdateEntityActions.request>,
) {
  try {
    const { organisation } = yield select((state: RootState) => state.openBankingDiretory);
    const { userInfo } = yield select((state: RootState) => state.membership);
    const { entity, getDefaultApis, optionalLink } = action.payload;
    const { id: organisationId, subdomain } = yield call(
      serviceDirectoryService.createOrUpdateEntity,
      entity,
    );

    if (getDefaultApis) {
      yield call(apiPortalService.getDefaultApis, organisationId);
    }

    yield call(
      membershipService.udpateOrganisation,
      organisationId,
      userInfo.userId,
      organisation?.shortName || entity.name,
    );

    optionalLink
      ? navigate(optionalLink)
      : (window.location.href = `https://${subdomain}.${env.domain}`);

    yield put(createOrUpdateEntityActions.success());
  } catch (err) {
    yield put(createOrUpdateEntityActions.failure(err));
  }
}

export function* fetchArticlesSaga(action: ActionType<typeof fetchArticlesActions.request>) {
  try {
    const { organisationId, isGlobal, pageNum, pageSize, searchText } = action.payload;
    const articles:  {
      data: Article[];
      paging: Paging;
  } = yield call(
      serviceDirectoryService.fetchArticles,
      organisationId,
      isGlobal,
      pageNum,
      pageSize,
      searchText,
    );
    yield put(fetchArticlesActions.success(articles));
  } catch (err) {
    yield put(fetchArticlesActions.failure(err));
  }
}

export function* loadMoreArticlesSaga(action: ActionType<typeof loadMoreArticlesActions.request>) {
  try {
    const { organisationId, isGlobal, pageNum, pageSize, searchText } = action.payload;
    const articles: {
      data: Article[];
      paging: Paging;
  } = yield call(
      serviceDirectoryService.fetchArticles,
      organisationId,
      isGlobal,
      pageNum,
      pageSize,
      searchText,
    );
    yield put(loadMoreArticlesActions.success(articles));
  } catch (err) {
    yield put(loadMoreArticlesActions.failure(err));
  }
}

export function* fetchArticleBySlugSaga(
  action: ActionType<typeof fetchArticlesBySlugActions.request>,
) {
  try {
    const { slug } = action.payload;
    const entity: Article = yield call(serviceDirectoryService.fetchArticlesBySlug, slug);
    yield put(fetchArticlesBySlugActions.success(entity));
  } catch (err) {
    yield put(fetchArticlesBySlugActions.failure(err));
  }
}

export function* createArticleSaga(action: ActionType<typeof createArticleActions.request>) {
  try {
    const { article, isGlobal } = action.payload;
    const organisationId: string = yield select((state: RootState) => state?.site?.organisation?.id);
    yield call(serviceDirectoryService.createArticle, article, organisationId);
    yield put(createArticleActions.success());

    // refetch articles
    yield put(fetchArticlesActions.request(organisationId, isGlobal, 0, PAGE_SIZE_DEFAULT));

    navigate(`/dashboard/my-articles`);
  } catch (err) {
    yield put(createArticleActions.failure(err));
  }
}

export function* editArticleSaga(action: ActionType<typeof editArticleActions.request>) {
  try {
    const { id, article, isGlobal } = action.payload;
    const organisationId : string = yield select((state: RootState) => state?.site?.organisation?.id);

    yield call(serviceDirectoryService.updateArticle, id, article, organisationId);
    yield put(editArticleActions.success());

    // refetch articles
    yield put(fetchArticlesActions.request(organisationId, isGlobal, 0, PAGE_SIZE_DEFAULT));

    navigate(`/dashboard/my-articles`);
  } catch (err) {
    yield put(editArticleActions.failure(err));
  }
}

export function* deleteArticleSaga(action: ActionType<typeof deleteArticleActions.request>) {
  try {
    const { id, isGlobal } = action.payload;
    const organisationId: string = yield select((state: RootState) => state?.site?.organisation?.id);

    yield call(serviceDirectoryService.deleteArticle, id, organisationId);
    yield put(deleteArticleActions.success());

    // refetch articles
    yield put(fetchArticlesActions.request(organisationId, isGlobal, 0, PAGE_SIZE_DEFAULT));

    navigate(`/dashboard/my-articles`);
  } catch (err) {
    yield put(deleteArticleActions.failure(err));
  }
}

export function* fetchAppsInfoSaga(action: ActionType<typeof fetchAppsInfoActions.request>) {
  try {
    const { organisationId } = action.payload;
    const appsInfo: AppsInfo = yield call(serviceDirectoryService.fetchAppsInfo, organisationId);
    yield put(fetchAppsInfoActions.success(sortBy(appsInfo, ['sequence'])));
  } catch (err) {
    yield put(fetchAppsInfoActions.failure(err));
  }
}

export function* createAppInfoSaga(action: ActionType<typeof createAppInfoActions.request>) {
  try {
    const { appInfo } = action.payload;
    const organisationId: string = yield select((state: RootState) => state.site.organisation.id);
    yield call(serviceDirectoryService.createAppInfo, appInfo, organisationId);
    yield put(createAppInfoActions.success());

    // refetch apps
    yield put(fetchAppsInfoActions.request(organisationId));

    navigate(`/dashboard/my-apps`);
  } catch (err) {
    yield put(createAppInfoActions.failure(err));
  }
}

export function* editAppInfoSaga(action: ActionType<typeof editAppInfoActions.request>) {
  try {
    const { id, appInfo } = action.payload;
    const organisationId: string = yield select((state: RootState) => state.site.organisation.id);

    yield call(serviceDirectoryService.updateAppInfo, id, appInfo, organisationId);
    yield put(editAppInfoActions.success());

    // refetch api docs
    yield put(fetchAppsInfoActions.request(organisationId));

    navigate(`/dashboard/my-apps`);
  } catch (err) {
    yield put(editAppInfoActions.failure(err));
  }
}

export function* deleteAppInfoSaga(action: ActionType<typeof deleteAppInfoActions.request>) {
  try {
    const { id } = action.payload;
    const organisationId: string = yield select((state: RootState) => state.site.organisation.id);

    yield call(serviceDirectoryService.deleteAppInfo, id, organisationId);
    yield put(deleteAppInfoActions.success());

    // refetch api docs
    yield put(fetchAppsInfoActions.request(organisationId));

    navigate(`/dashboard/my-apps`);
  } catch (err) {
    yield put(deleteAppInfoActions.failure(err));
  }
}

export function* scrapeAppInfoSaga(action: ActionType<typeof scrapeAppInfoActions.request>) {
  try {
    const { id, playStoreLink, appStoreLink } = action.payload;
    const organisationId : string = yield select((state: RootState) => state.site.organisation.id);
    yield call(serviceDirectoryService.scrapeAppInfo, id, appStoreLink, playStoreLink);
    yield put(scrapeAppInfoActions.success());

    // refetch api docs
    yield put(fetchAppsInfoActions.request(organisationId));

    // navigate(`/dashboard/my-apps`);
  } catch (err) {
    yield put(scrapeAppInfoActions.failure(err));
  }
}

export function* fetchCategoriesSaga(action: ActionType<typeof fetchCategoriesActions.request>) {
  try {
    const { entryType, industry } = action.payload;
    const categories: Categories = yield call(serviceDirectoryService.fetchCategories, entryType, industry);
    yield put(fetchCategoriesActions.success(categories));
  } catch (err) {
    yield put(fetchCategoriesActions.failure(err));
  }
}
export function* fetchCategorySaga(action: ActionType<typeof fetchCategoryByIdActions.request>) {
  try {
    const { id } = action.payload;
    const category: Category = yield call(serviceDirectoryService.fetchCategory, id);
    yield put(fetchCategoryByIdActions.success(category));
  } catch (err) {
    yield put(fetchCategoryByIdActions.failure(err));
  }
}

export function* createCategorySaga(action: ActionType<typeof createCategoryActions.request>) {
  try {
    const { categoryInfo } = action.payload;
    yield call(serviceDirectoryService.createCategory, categoryInfo);
    yield put(createCategoryActions.success());

    // refetch categories
    yield put(fetchCategoriesActions.request(categoryInfo.entryType));

    navigate(
      `/dashboard/${
        categoryInfo.entryType === EntryType.API ? 'api-categories' : 'app-categories'
      }`,
    );
  } catch (err) {
    yield put(createCategoryActions.failure(err));
  }
}

export function* editCategorySaga(action: ActionType<typeof editCategoryActions.request>) {
  try {
    const { id, categoryInfo } = action.payload;

    yield call(serviceDirectoryService.updateCategory, id, categoryInfo);
    yield put(editCategoryActions.success());

    // refetch categories
    yield put(fetchCategoriesActions.request(categoryInfo.entryType));

    navigate(
      `/dashboard/${
        categoryInfo.entryType === EntryType.API ? 'api-categories' : 'app-categories'
      }`,
    );
  } catch (err) {
    yield put(editCategoryActions.failure(err));
  }
}

export function* deleteCactegorySaga(action: ActionType<typeof deleteCategoryActions.request>) {
  try {
    const { id, entryType } = action.payload;

    yield call(serviceDirectoryService.deleteCategory, id);
    yield put(deleteCategoryActions.success());

    // refetch categories
    yield put(fetchCategoriesActions.request(entryType));

    navigate(`/dashboard/${entryType === EntryType.API ? 'api-categories' : 'app-categories'}`);
  } catch (err) {
    yield put(deleteCategoryActions.failure(err));
  }
}

export function* rateEntitySaga(action: ActionType<typeof rateEntityActions.request>) {
  try {
    const { id, rating } = action.payload;
    yield call(serviceDirectoryService.rateEntity, id, rating);
    yield put(rateEntityActions.success());
  } catch (err) {
    yield put(rateEntityActions.failure(err));
  }
}

export function* rateAssetSaga(action: ActionType<typeof rateAssetActions.request>) {
  try {
    const { formAsset, socialUserToken } = action.payload;
    yield call(serviceDirectoryService.rateAsset, formAsset, socialUserToken);
    yield put(rateAssetActions.success());
  } catch (err) {
    yield put(rateAssetActions.failure(err));
  }
}

export function* fetchAssetSocialSaga(action: ActionType<typeof fetchAssetSocialActions.request>) {
  try {
    const { socialUserToken } = action.payload;
    const response: AssetsSocial = yield call(serviceDirectoryService.fetchAssetSocial, socialUserToken);
    yield put(fetchAssetSocialActions.success(response));
  } catch (err) {
    yield put(fetchAssetSocialActions.failure(err));
  }
}

export function* fetchListEntitySaga(action: ActionType<typeof fetchListEntityActions.request>) {
  const { pageNum, pageSize, searchEntity } = action.payload;
  try {
    const { data, paging } = yield call(
      serviceDirectoryService.fetchListEntity,
      pageNum,
      pageSize,
      searchEntity,
    );
    yield put(fetchListEntityActions.success({ data, paging }));
  } catch (err) {
    const errorResponse = (err as AxiosError)?.response;
    const message: any = (err as any)?.message;
    const errors = {
      status: errorResponse?.status,
      message:
        errorResponse?.data?.message ||
        errorResponse?.data?.error_description ||
        errorResponse?.data?.errors?.[0].message ||
        message,
    };
    yield put(fetchListEntityActions.failure(errors));
  }
}

export function* fetchListUserSocialSaga(
  action: ActionType<typeof fetchListUserSocialActions.request>,
) {
  const { pageNum, pageSize, searchText, sort } = action.payload;
  try {
    const { data, paging } = yield call(
      serviceDirectoryService.fetchListUserSocial,
      pageNum,
      pageSize,
      sort,
      searchText,
    );
    yield put(fetchListUserSocialActions.success({ data, paging }));
  } catch (err) {
    const errorResponse = (err as AxiosError)?.response;
    const message: any = (err as any)?.message;
    const errors = {
      status: errorResponse?.status,
      message:
        errorResponse?.data?.message ||
        errorResponse?.data?.error_description ||
        errorResponse?.data?.errors?.[0].message ||
        message,
    };
    yield put(fetchListEntityActions.failure(errors));
  }
}

export function* deleteEntitySaga(action: ActionType<typeof deleteEntityActions.request>) {
  const { entityId } = action.payload;
  try {
    yield call(serviceDirectoryService.deleteEntity, entityId);
    yield put(deleteEntityActions.success(entityId));
  } catch (err) {
    yield put(deleteEntityActions.failure(err));
  }
}

export function* fetchComplianceLevelsSaga() {
  try {
    const data: ComplianceLevel[] = yield call(serviceDirectoryService.fetchComplianceLevels);
    yield put(fetchComplianceLevelsActions.success(data));
  } catch (err) {
    yield put(fetchComplianceLevelsActions.failure(err));
  }
}

export function* generateIndexesSaga() {
  try {
    yield call(serviceDirectoryService.generateIndexes);
    yield put(generateIndexesActions.success());
  } catch (err) {
    yield put(generateIndexesActions.failure(err));
  }
}

export function* fetchDocsOverviewSaga(
  action: ActionType<typeof fetchDocsOverviewActions.request>,
) {
  try {
    const { organisationId } = action.payload;
    const data: DocOverview[] = yield call(serviceDirectoryService.fetchDocsOverview, organisationId);
    yield put(fetchDocsOverviewActions.success(data));
  } catch (err) {
    yield put(fetchDocsOverviewActions.failure(err));
  }
}

export function* updateDocOverviewSaga(
  action: ActionType<typeof updateDocOverviewActions.request>,
) {
  try {
    const { organisationId, tags, docString } = action.payload;
    yield call(serviceDirectoryService.updateDocOverview, organisationId, tags, docString);
    yield put(updateDocOverviewActions.success());
    yield put(fetchDocsOverviewActions.request(organisationId));
  } catch (err) {
    yield put(updateDocOverviewActions.failure(err));
  }
}

export function* createAndScrapeAppInfoSaga(
  action: ActionType<typeof createAndScrapeAppInfoActions.request>,
) {
  try {
    const { urlScrape } = action.payload;
    const organisationId: string = yield select((state: RootState) => state.site.organisation.id);
    const data: {id: string} = yield call(
      serviceDirectoryService.createAndScrapeAppInfo,
      organisationId,
      urlScrape,
    );
    yield put(createAndScrapeAppInfoActions.success());
    // refetch app info
    yield put(fetchAppsInfoActions.request(organisationId));
    if (data?.id) {
      navigate(`/dashboard/my-apps/edit/${data?.id}`);
    }
  } catch (err) {
    yield put(createAndScrapeAppInfoActions.failure(err));
  }
}

export function* getFeatureItemsSaga() {
  try {
    const response: FeatureItems = yield call(serviceDirectoryService.fetchFeatureItem);
    yield put(getFeatureItemsActions.success(response));
  } catch (err) {
    yield put(getFeatureItemsActions.failure(err));
  }
}

export default function* root() {
  yield all([
    takeLatest(fetchEntityActions.request.type, fetchEntitySaga),
    takeLatest(fetchEntityByIdActions.request.type, fetchEntityByIdSaga),
    takeLatest(deleteEntityActions.request.type, deleteEntitySaga),
    takeLatest(createOrUpdateEntityActions.request.type, createOrUpdateEntitySaga),
    takeLatest(fetchAppsInfoActions.request.type, fetchAppsInfoSaga),
    takeLatest(fetchCategoriesActions.request.type, fetchCategoriesSaga),
    takeLatest(fetchCategoryByIdActions.request.type, fetchCategorySaga),
    takeLatest(createAppInfoActions.request.type, createAppInfoSaga),
    takeLatest(editAppInfoActions.request.type, editAppInfoSaga),
    takeLatest(deleteAppInfoActions.request.type, deleteAppInfoSaga),
    takeLatest(scrapeAppInfoActions.request.type, scrapeAppInfoSaga),
    takeLatest(rateEntityActions.request.type, rateEntitySaga),
    takeLatest(fetchListEntityActions.request.type, fetchListEntitySaga),
    takeLatest(rateAssetActions.request.type, rateAssetSaga),
    takeLatest(fetchAssetSocialActions.request.type, fetchAssetSocialSaga),
    takeLatest(fetchComplianceLevelsActions.request.type, fetchComplianceLevelsSaga),
    takeLatest(generateIndexesActions.request.type, generateIndexesSaga),
    takeLatest(fetchListUserSocialActions.request.type, fetchListUserSocialSaga),
    takeLatest(fetchArticlesActions.request.type, fetchArticlesSaga),
    takeLatest(loadMoreArticlesActions.request.type, loadMoreArticlesSaga),
    takeLatest(createArticleActions.request.type, createArticleSaga),
    takeLatest(editArticleActions.request.type, editArticleSaga),
    takeLatest(deleteArticleActions.request.type, deleteArticleSaga),
    takeLatest(fetchArticlesBySlugActions.request.type, fetchArticleBySlugSaga),
    takeLatest(fetchDocsOverviewActions.request.type, fetchDocsOverviewSaga),
    takeLatest(updateDocOverviewActions.request.type, updateDocOverviewSaga),
    takeLatest(createCategoryActions.request.type, createCategorySaga),
    takeLatest(editCategoryActions.request.type, editCategorySaga),
    takeLatest(deleteCategoryActions.request.type, deleteCactegorySaga),
    takeLatest(createAndScrapeAppInfoActions.request.type, createAndScrapeAppInfoSaga),
    takeLatest(getFeatureItemsActions.request.type, getFeatureItemsSaga),
  ]);
}
